import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Activity, TrendingDown, Star, Plus, FileText, Droplets, Bone, Flame, Heart, Scale, Brain, Dumbbell, X, Download, Clock, CheckCircle, FileCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';
import { ReviewsPanel } from '@/components/ReviewsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BIA_FIELDS = [
  { key: 'weight', label: 'Weight', unit: 'kg', icon: Scale, color: '#D4AF37', group: 'core' },
  { key: 'body_fat_pct', label: 'Body Fat', unit: '%', icon: Activity, color: '#E2B4BD', group: 'core' },
  { key: 'body_fat_mass', label: 'Fat Mass', unit: 'kg', icon: Activity, color: '#E07A7A', group: 'core' },
  { key: 'muscle_mass', label: 'Muscle Mass', unit: 'kg', icon: Dumbbell, color: '#7AE0A0', group: 'muscle' },
  { key: 'fat_free_mass', label: 'Fat-Free Mass', unit: 'kg', icon: Dumbbell, color: '#7AC8E0', group: 'muscle' },
  { key: 'total_body_water', label: 'Body Water', unit: 'L', icon: Droplets, color: '#5BA8D4', group: 'water' },
  { key: 'body_water_pct', label: 'Body Water', unit: '%', icon: Droplets, color: '#3D8BB5', group: 'water' },
  { key: 'bone_mass', label: 'Bone Mass', unit: 'kg', icon: Bone, color: '#C4B5A0', group: 'structure' },
  { key: 'bmi', label: 'BMI', unit: 'kg/m²', icon: Scale, color: '#D4AF37', group: 'metabolic' },
  { key: 'bmr', label: 'BMR', unit: 'kcal', icon: Flame, color: '#E5A040', group: 'metabolic' },
  { key: 'visceral_fat', label: 'Visceral Fat', unit: 'level', icon: Heart, color: '#D45A5A', group: 'metabolic' },
  { key: 'metabolic_age', label: 'Metabolic Age', unit: 'yrs', icon: Brain, color: '#B07AE0', group: 'metabolic' },
  { key: 'protein_pct', label: 'Protein', unit: '%', icon: Dumbbell, color: '#8CC77A', group: 'additional' },
  { key: 'phase_angle', label: 'Phase Angle', unit: '°', icon: Heart, color: '#7AE0D4', group: 'additional' },
];

const CHART_GROUPS = [
  { id: 'core', fields: ['weight', 'body_fat_pct'] },
  { id: 'composition', fields: ['muscle_mass', 'fat_free_mass', 'body_fat_mass'] },
  { id: 'water', fields: ['total_body_water', 'body_water_pct'] },
  { id: 'metabolic', fields: ['bmi', 'bmr', 'visceral_fat', 'metabolic_age'] },
  { id: 'additional', fields: ['bone_mass', 'protein_pct', 'phase_angle'] },
];

const DashboardPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeChart, setActiveChart] = useState('core');
  const { t, isAr, lang, fmtDate, fmtDateTime } = useLang();
  const fl = (f) => `${t(`bia.${f.key}`)} (${t(`unit.${f.unit}`)})`;

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (token) fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(res.data);
    } catch {
      toast.error(t('dash.load_fail'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    BIA_FIELDS.forEach(f => {
      if (form[f.key] !== undefined && form[f.key] !== '') payload[f.key] = parseFloat(form[f.key]);
    });
    if (form.notes) payload.notes = form.notes;
    if (Object.keys(payload).filter(k => k !== 'notes').length === 0) {
      toast.error(t('bia.need_one'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/dashboard/progress`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('bia.logged'));
      setForm({});
      setShowForm(false);
      fetchDashboard();
    } catch {
      toast.error(t('bia.log_fail'));
    }
    setSubmitting(false);
  };

  const chartData = useMemo(() => {
    if (!dashboard?.progress) return [];
    return [...dashboard.progress].reverse().map(p => ({
      date: fmtDate(p.created_at, { month: 'short', day: 'numeric' }),
      ...BIA_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: p[f.key] ?? null }), {}),
    }));
  }, [dashboard, fmtDate]);

  const latestEntry = dashboard?.progress?.[0];

  const getLatestValue = (key) => {
    if (!latestEntry) return null;
    return latestEntry[key] ?? null;
  };

  const activeChartGroup = CHART_GROUPS.find(g => g.id === activeChart);

  if (authLoading || !dashboard) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><p className="text-[#A3A19C]">{t('common.loading')}</p></div>;
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="dashboard-page">
      {/* Header */}
      <section className="py-10 lg:py-14 bg-[#121215] border-b border-[rgba(255,255,255,0.06)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="overline mb-2">{t('dash.overline')}</p>
              <h1 className="text-3xl lg:text-4xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">
                {t('dash.welcome')} <span className="gold-gradient-text">{dashboard.user.name}</span>
              </h1>
              <p className="text-sm text-[#A3A19C] mt-1">{t('dash.sub')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
            <button onClick={() => setShowForm(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="add-bia-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t('dash.log')}
            </button>
            <a href={`${API}/dashboard/report/pdf?token=${token}&lang=${lang}`} target="_blank" rel="noopener noreferrer"
              className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="download-bia-report">
              <Download className="w-4 h-4" strokeWidth={1.5} /> {t('common.download_report')}
            </a>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* BIA Snapshot Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-10" data-testid="bia-snapshot">
          {[
            { key: 'weight', label: 'bia.weight', unit: 'kg', color: '#D4AF37' },
            { key: 'body_fat_pct', label: 'bia.body_fat_pct', unit: '%', color: '#E2B4BD' },
            { key: 'muscle_mass', label: 'bia.muscle_short', unit: 'kg', color: '#7AE0A0' },
            { key: 'total_body_water', label: 'bia.water_short', unit: 'L', color: '#5BA8D4' },
            { key: 'bmi', label: 'bia.bmi_short', unit: '', color: '#D4AF37' },
            { key: 'visceral_fat', label: 'bia.visceral_short', unit: '', color: '#D45A5A' },
            { key: 'metabolic_age', label: 'bia.metabolic_short', unit: 'yrs', color: '#B07AE0' },
          ].map(item => {
            const val = getLatestValue(item.key);
            return (
              <div key={item.key} className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-4" data-testid={`snapshot-${item.key}`}>
                <p className="text-[10px] text-[#A3A19C] uppercase tracking-wider mb-2">{t(item.label)}</p>
                <p className="text-2xl font-['Cormorant_Garamond'] font-bold" style={{ color: val ? item.color : '#1A1A1E' }}>
                  {val !== null ? val : '--'}
                </p>
                <p className="text-[10px] text-[#A3A19C] mt-0.5">{t(`unit.${item.unit}`)}</p>
              </div>
            );
          })}
        </div>

        {/* BIA Input Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[#08080A]/80 backdrop-blur-sm px-4" onClick={() => setShowForm(false)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
                data-testid="bia-form-modal"
              >
                <div className="sticky top-0 bg-[#121215] border-b border-[rgba(255,255,255,0.06)] px-8 py-5 flex items-center justify-between z-10">
                  <div>
                    <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('dash.log')}</h3>
                    <p className="text-xs text-[#A3A19C]">{t('dash.log_sub')}</p>
                  </div>
                  <button onClick={() => setShowForm(false)} className="text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300">
                    <X className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6" data-testid="bia-form">
                  {/* Core Measurements */}
                  <FormSection title={t('bia.sec_core')} subtitle={t('bia.sec_core_sub')}>
                    <div className="grid grid-cols-3 gap-3">
                      <BIAInput label={t('bia.weight')} unit={t('unit.kg')} field="weight" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.body_fat_pct')} unit={t('unit.%')} field="body_fat_pct" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.body_fat_mass')} unit={t('unit.kg')} field="body_fat_mass" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Muscle & Lean */}
                  <FormSection title={t('bia.sec_muscle')} subtitle={t('bia.sec_muscle_sub')}>
                    <div className="grid grid-cols-2 gap-3">
                      <BIAInput label={t('bia.muscle_mass')} unit={t('unit.kg')} field="muscle_mass" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.fat_free_mass')} unit={t('unit.kg')} field="fat_free_mass" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Hydration */}
                  <FormSection title={t('bia.sec_water')} subtitle={t('bia.sec_water_sub')}>
                    <div className="grid grid-cols-2 gap-3">
                      <BIAInput label={t('bia.total_body_water')} unit={t('unit.L')} field="total_body_water" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.body_water_pct')} unit={t('unit.%')} field="body_water_pct" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Structure */}
                  <FormSection title={t('bia.sec_bone')} subtitle={t('bia.sec_bone_sub')}>
                    <div className="grid grid-cols-1 gap-3">
                      <BIAInput label={t('bia.bone_mass')} unit={t('unit.kg')} field="bone_mass" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Metabolic */}
                  <FormSection title={t('bia.sec_metabolic')} subtitle={t('bia.sec_metabolic_sub')}>
                    <div className="grid grid-cols-2 gap-3">
                      <BIAInput label={t('bia.bmi')} unit={t('unit.kg/m²')} field="bmi" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.bmr')} unit={t('unit.kcal')} field="bmr" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.visceral_fat')} unit={t('unit.level')} field="visceral_fat" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.metabolic_age')} unit={t('unit.yrs')} field="metabolic_age" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Additional */}
                  <FormSection title={t('bia.sec_additional')} subtitle={t('bia.sec_additional_sub')}>
                    <div className="grid grid-cols-2 gap-3">
                      <BIAInput label={t('bia.protein_pct')} unit={t('unit.%')} field="protein_pct" form={form} setForm={setForm} />
                      <BIAInput label={t('bia.phase_angle')} unit={t('unit.°')} field="phase_angle" form={form} setForm={setForm} />
                    </div>
                  </FormSection>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs text-[#A3A19C] mb-1.5">{t('common.notes')}</label>
                    <input type="text" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}
                      data-testid="bia-notes"
                      className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2.5 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                      placeholder={t('bia.notes_ph')} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button type="submit" disabled={submitting} className="btn-primary text-sm" data-testid="bia-submit">
                      {submitting ? t('common.saving') : t('bia.save')}
                    </button>
                    <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">{t('common.cancel')}</button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6" data-testid="bia-chart">
            {/* Chart Group Tabs */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('dash.trends')}</h3>
              <div className="flex gap-1 flex-wrap" data-testid="chart-tabs">
                {CHART_GROUPS.map(g => (
                  <button key={g.id} onClick={() => setActiveChart(g.id)} data-testid={`chart-tab-${g.id}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-300 ${
                      activeChart === g.id ? 'bg-[#D4AF37] text-[#08080A]' : 'text-[#A3A19C] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(212,175,55,0.3)]'
                    }`}>
                    {t(`chart.${g.id}`)}
                  </button>
                ))}
              </div>
            </div>

            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    {activeChartGroup?.fields.map((fKey, i) => {
                      const field = BIA_FIELDS.find(f => f.key === fKey);
                      return (
                        <linearGradient key={fKey} id={`grad-${fKey}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={field?.color || '#D4AF37'} stopOpacity={0.15} />
                          <stop offset="95%" stopColor={field?.color || '#D4AF37'} stopOpacity={0} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" reversed={isAr} tick={{ fill: '#A3A19C', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                  <YAxis orientation={isAr ? 'right' : 'left'} tick={{ fill: '#A3A19C', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                  <Tooltip
                    contentStyle={{
                      background: theme === 'dark' ? '#121215' : '#F0EDE8',
                      border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                      borderRadius: 8,
                      color: theme === 'dark' ? '#F5F2EB' : '#1A1A1E',
                      fontSize: 12
                    }}
                    labelStyle={{ color: '#D4AF37' }}
                  />
                  {activeChartGroup?.fields.map((fKey) => {
                    const field = BIA_FIELDS.find(f => f.key === fKey);
                    return (
                      <Area
                        key={fKey}
                        type="monotone"
                        dataKey={fKey}
                        stroke={field?.color || '#D4AF37'}
                        strokeWidth={2}
                        fill={`url(#grad-${fKey})`}
                        dot={{ fill: field?.color || '#D4AF37', r: 3 }}
                        name={fl(field)}
                        connectNulls
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Scale className="w-10 h-10 text-[#1A1A1E] mb-4" strokeWidth={1.5} />
                <p className="text-sm text-[#A3A19C]">{t('dash.no_data')}</p>
              </div>
            )}

            {/* Chart Legend */}
            {chartData.length > 0 && (
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
                {activeChartGroup?.fields.map(fKey => {
                  const field = BIA_FIELDS.find(f => f.key === fKey);
                  return (
                    <div key={fKey} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: field?.color }} />
                      <span className="text-xs text-[#A3A19C]">{fl(field)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Side Panel */}
          <div className="space-y-4">
            {/* Full BIA Breakdown */}
            <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="bia-breakdown">
              <h3 className="text-base font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-4">{t('dash.latest')}</h3>
              {latestEntry ? (
                <div className="space-y-2">
                  {BIA_FIELDS.map(f => {
                    const val = latestEntry[f.key];
                    if (val === null || val === undefined) return null;
                    return (
                      <div key={f.key} className="flex items-center justify-between py-1.5 border-b border-[rgba(255,255,255,0.04)] last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.color }} />
                          <span className="text-xs text-[#A3A19C]">{t(`bia.${f.key}`)}</span>
                        </div>
                        <span className="text-sm font-medium text-[#F5F2EB]">{val} <span className="text-[10px] text-[#A3A19C]">{t(`unit.${f.unit}`)}</span></span>
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-[#8C6C73] pt-2">
                    {fmtDateTime(latestEntry.created_at)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-[#A3A19C] py-4">{t('dash.no_readings')}</p>
              )}
            </div>

            {/* Recommended Programs */}
            <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="recommendation-section">
              <h3 className="text-base font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-3">{t('dash.recommended')}</h3>
              {dashboard.latest_recommendation ? (
                <div className="space-y-2">
                  {dashboard.latest_recommendation.programs.map((slug) => (
                    <div key={slug} className="flex items-center gap-2 py-1.5 border-b border-[rgba(255,255,255,0.04)] last:border-0">
                      <Star className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-xs text-[#F5F2EB] capitalize">{slug.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FileText className="w-6 h-6 text-[#1A1A1E] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-xs text-[#A3A19C] mb-3">{t('dash.take_first')}</p>
                  <Link to="/questionnaire" className="btn-secondary text-[10px] py-1.5 px-4" data-testid="dashboard-take-assessment">{t('dash.take')}</Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assessment Contracts */}
        {(dashboard.assessments?.length > 0) && (
          <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden mb-10" data-testid="assessment-contracts">
            <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.06)]">
              <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('dash.contracts')}</h3>
              <p className="text-xs text-[#A3A19C]">{t('dash.contracts_sub')}</p>
            </div>
            <div className="divide-y divide-[rgba(255,255,255,0.04)]">
              {dashboard.assessments.map(a => {
                const isSigned = a.coach_signed;
                return (
                  <div key={a.id} className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 hover:bg-[#1A1A1E] transition-colors duration-200" data-testid={`contract-${a.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isSigned ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                        {isSigned ? <FileCheck className="w-5 h-5 text-green-400" strokeWidth={1.5} /> : <Clock className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />}
                      </div>
                      <div>
                        <p className="text-sm text-[#F5F2EB] font-medium">{t('dash.contract_name')}</p>
                        <p className="text-xs text-[#A3A19C]">
                          {t('dash.submitted_on', { date: fmtDate(a.submitted_at) })}
                          {isSigned && a.coach_signed_at && t('dash.signed_by_on', { name: a.coach_signer_name || t('coach'), date: fmtDate(a.coach_signed_at) })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ms-14 sm:ms-0">
                      <span className={`text-[10px] px-3 py-1 rounded-full font-semibold ${isSigned ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                        {isSigned ? t('dash.signed') : t('dash.pending')}
                      </span>
                      {isSigned ? (
                        <a href={`${API}/assessment/${a.id}/pdf?token=${token}&lang=${lang}`} target="_blank" rel="noopener noreferrer"
                          className="btn-primary text-xs py-2 px-4 inline-flex items-center gap-1.5" data-testid={`download-contract-${a.id}`}>
                          <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('dash.download_pdf')}
                        </a>
                      ) : (
                        <span className="text-xs text-[#8C6C73] flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('dash.awaiting')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ReviewsPanel token={token} />

        {/* History Table */}
        <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden" data-testid="bia-history">
          <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.06)]">
            <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('bia.history')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[rgba(255,255,255,0.06)]">
                  <th className="text-start px-4 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.date')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.weight')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.fat_pct_short')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.muscle_short')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.water_short')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.bmi_short')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.visceral_short')}</th>
                  <th className="text-end px-3 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('bia.metabolic_short')}</th>
                  <th className="text-start px-4 py-3 text-[10px] text-[#A3A19C] font-semibold uppercase tracking-wider">{t('common.notes')}</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.progress.map((entry) => (
                  <tr key={entry.id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[#1A1A1E] transition-colors duration-200">
                    <td className="px-4 py-3 text-xs text-[#A3A19C]">{fmtDate(entry.created_at)}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#F5F2EB]">{entry.weight ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#E2B4BD]">{entry.body_fat_pct ?? entry.body_fat ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#7AE0A0]">{entry.muscle_mass ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#5BA8D4]">{entry.total_body_water ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#D4AF37]">{entry.bmi ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#D45A5A]">{entry.visceral_fat ?? '-'}</td>
                    <td className="text-end px-3 py-3 text-xs text-[#B07AE0]">{entry.metabolic_age ?? '-'}</td>
                    <td className="px-4 py-3 text-xs text-[#8C6C73] max-w-[120px] truncate">{entry.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dashboard.progress.length === 0 && <p className="text-sm text-[#A3A19C] p-6 text-center">{t('dash.history_empty')}</p>}
        </div>
      </div>
    </div>
  );
};

const FormSection = ({ title, subtitle, children }) => (
  <div>
    <div className="flex items-baseline gap-2 mb-3">
      <h4 className="text-sm font-semibold text-[#D4AF37]">{title}</h4>
      <span className="text-[10px] text-[#8C6C73]">{subtitle}</span>
    </div>
    {children}
  </div>
);

const BIAInput = ({ label, unit, field, form, setForm }) => (
  <div>
    <label className="block text-[10px] text-[#A3A19C] mb-1">{label} <span className="text-[#8C6C73]">({unit})</span></label>
    <input
      type="number"
      step="0.1"
      value={form[field] || ''}
      onChange={e => setForm({ ...form, [field]: e.target.value })}
      data-testid={`bia-${field}`}
      className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
      placeholder="--"
    />
  </div>
);

export default DashboardPage;
